/**
 * Datos de la empresa (bloque 13): razón social, RIF, dirección fiscal, teléfonos y correo. Salen en la cabecera de las
 * notas de entrega, cotizaciones, despachos y comprobantes de retención, y en las declaraciones. La factura no los
 * imprime: su papel ya los trae. Solo la dueña los cambia; queda en auditoría.
 */
import type { EmpresaDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, mensajeDeError, patch } from '@/lib/api.ts';

type Formulario = { razon_social: string; rif: string; direccion_fiscal: string; telefono: string; correo: string };
const deEmpresa = (e: EmpresaDTO): Formulario => ({ razon_social: e.razon_social, rif: e.rif, direccion_fiscal: e.direccion_fiscal, telefono: e.telefono ?? '', correo: e.correo ?? '' });

export function EmpresaCard() {
  const empresa = useQuery({ queryKey: ['consolidado', 'empresa'], queryFn: () => get<EmpresaDTO>('/consolidado/empresa') });
  return (
    <Card data-testid="empresa">
      <CardHeader>
        <div className="flex items-center gap-1"><CardTitle>Datos de la empresa</CardTitle><AyudaVentana tema="configuracion.empresa" /></div>
        <CardDescription>Salen en la cabecera de notas, cotizaciones, despachos y comprobantes de retención, y en las declaraciones. La factura usa los del papel de la imprenta.</CardDescription>
      </CardHeader>
      <CardContent>
        <EstadoQuery cargando={empresa.isLoading} error={empresa.error} datos={empresa.data} reintentar={() => void empresa.refetch()}>
          {(e) => <FormularioEmpresa key={e.actualizado_en} empresa={e} />}
        </EstadoQuery>
      </CardContent>
    </Card>
  );
}

/** El formulario nace con lo guardado (una instancia por versión: `key`). */
function FormularioEmpresa({ empresa }: { empresa: EmpresaDTO }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Formulario>(() => deEmpresa(empresa));
  const set = <K extends keyof Formulario>(k: K, v: Formulario[K]) => setF((x) => ({ ...x, [k]: v }));
  const original = deEmpresa(empresa);
  const cambio = (Object.keys(f) as (keyof Formulario)[]).some((k) => f[k] !== original[k]);
  const guardar = useMutation({
    mutationFn: () => patch<EmpresaDTO>('/consolidado/empresa', { razon_social: f.razon_social.trim(), rif: f.rif.trim(), direccion_fiscal: f.direccion_fiscal.trim(), telefono: f.telefono.trim() || null, correo: f.correo.trim() || null }),
    onSuccess: (e) => {
      toast.success('Datos de la empresa guardados', { description: 'Quedan en auditoría.' });
      qc.setQueryData(['consolidado', 'empresa'], e);
      void qc.invalidateQueries({ queryKey: ['empresa'] });
      void qc.invalidateQueries({ queryKey: ['compras', 'empresa'] });
    },
    onError: (e) => toast.error('No se pudo guardar', { description: mensajeDeError(e) }),
  });
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Campo etiqueta="Razón social" htmlFor="emp-razon"><Input id="emp-razon" value={f.razon_social} onChange={(e) => set('razon_social', e.target.value)} maxLength={160} data-testid="emp-razon" /></Campo>
      <Campo etiqueta="RIF" htmlFor="emp-rif" ayuda="Formato J-12345678-9"><Input id="emp-rif" value={f.rif} onChange={(e) => set('rif', e.target.value.toUpperCase())} maxLength={14} data-testid="emp-rif" /></Campo>
      <Campo etiqueta="Dirección fiscal" htmlFor="emp-direccion" className="md:col-span-2"><Input id="emp-direccion" value={f.direccion_fiscal} onChange={(e) => set('direccion_fiscal', e.target.value)} maxLength={300} data-testid="emp-direccion" /></Campo>
      <Campo etiqueta="Teléfonos" htmlFor="emp-telefono"><Input id="emp-telefono" value={f.telefono} onChange={(e) => set('telefono', e.target.value)} maxLength={80} data-testid="emp-telefono" /></Campo>
      <Campo etiqueta="Correo" htmlFor="emp-correo"><Input id="emp-correo" type="email" value={f.correo} onChange={(e) => set('correo', e.target.value)} maxLength={160} data-testid="emp-correo" /></Campo>
      <p className="text-xs text-muted-foreground md:col-span-2" data-testid="emp-especial">
        {empresa.contribuyente_especial ? 'Contribuyente especial: agente de retención del IVA.' : 'Contribuyente ordinario.'} Cambiar el RIF o la razón social cambia también lo que sale al reimprimir notas, cotizaciones y comprobantes anteriores.
      </p>
      <div className="md:col-span-2">
        <Button disabled={!cambio || f.razon_social.trim().length < 2 || f.direccion_fiscal.trim().length < 5} cargando={guardar.isPending} onClick={() => guardar.mutate()} data-testid="emp-guardar">Guardar</Button>
      </div>
    </div>
  );
}
